import React from 'react';
import { Editor, Frame, Element, type SerializedNodes } from '@craftjs/core';
import { RenderProvider, type RenderContextType } from '../../context/RenderContext';

export interface CraftRendererProps {
  // CraftJS project JSON as a string. Project data as an object will be
  // serialized on your behalf. Leave empty for a default empty project.
  craftJson?: SerializedNodes | string;

  // Data made available to components when rendering.
  context: RenderContextType;

  // Required when rendering on the server.
  // This is equivalent to the CraftJS Editor#resolver
  componentResolver?: Record<string, React.ComponentType>;

  // Children are exposed only so that the Builder App can inject a the
  // Page component as the root of the project when no Craft JSON exists.
  children?: React.ReactNode;
}

const sanitizeCraftJson = (craftJson?: SerializedNodes | string): string | undefined => {
  if (!craftJson) return undefined;
  if (typeof craftJson === 'string') {
    return craftJson === '' ? undefined : craftJson;
  }
  return JSON.stringify(craftJson);
};

/**
 * This is the root rendering component for a project in Builder4. It is used
 * to render a page in the Builder4 application (client) and the backend rendering
 * service (server). When rendering on the client, you must include a CraftJS Editor
 * above this in the component tree. This allows you to wrap more of the application
 * with the Editor state, required for things like undo/redo in the navbar. This is
 * not a requirement on the server, but you must provide the component resolver so
 * user components can be discovered.
 */
export const CraftRenderer: React.FC<CraftRendererProps> = ({
  craftJson,
  componentResolver,
  context,
  children,
}) => {
  // The CraftJS project data must be serialized JSON or undefined
  const data = sanitizeCraftJson(craftJson);

  // Server and inspiration-preview render without a parent Editor — provide a componentResolver so user components can be discovered.
  if (context.environment === 'server' || context.environment === 'inspiration-preview') {
    if (!data) {
      throw new Error('CraftRenderer: craftJson is required for server-side rendering');
    }
    if (!componentResolver) {
      throw new Error('CraftRenderer: componentResolver is required for server-side rendering');
    }
    return (
      <Editor resolver={componentResolver} enabled={context.isEditable}>
        <RenderProvider value={context}>
          <Frame data={data} />
        </RenderProvider>
      </Editor>
    );
  }

  return (
    <RenderProvider value={context}>
      <Frame data={data}>{children || <Element canvas></Element>}</Frame>
    </RenderProvider>
  );
};

export default React.memo(CraftRenderer, (prevProps, nextProps) => {
  return prevProps.context === nextProps.context;
});
